import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';

export const useMaintenanceMode = () => {
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const { i18n } = useTranslation();

  const getContentValue = useCallback((row: any, baseField: string): string => {
    if (!row) return '';
    const currentLang = i18n.language?.split('-')[0] || 'pt';

    if (currentLang === 'en') {
      return row[`${baseField}_en`] || row[baseField] || '';
    } else if (currentLang === 'es') {
      return row[`${baseField}_es`] || row[baseField] || '';
    }
    return row[baseField] || '';
  }, [i18n.language]);

  const fetchMaintenanceMode = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('site_content')
        .select('*')
        .in('content_key', ['maintenance_mode_enabled', 'maintenance_message']);

      if (error) throw error;

      if (data) {
        const enabledRow = data.find(row => row.content_key === 'maintenance_mode_enabled');
        const messageRow = data.find(row => row.content_key === 'maintenance_message');

        setIsEnabled((enabledRow?.content_value || '') === 'true');
        setMessage(getContentValue(messageRow, 'content_value'));
      }
    } catch (error) {
      // Silently fail - use default state
    } finally {
      setIsLoading(false);
    }
  }, [getContentValue]);

  useEffect(() => {
    fetchMaintenanceMode();

    // Poll every 3 seconds as a fallback (works on all browsers/devices)
    const pollInterval = setInterval(fetchMaintenanceMode, 3000);

    // Refetch when window gains focus (desktop browsers)
    const handleFocus = () => {
      fetchMaintenanceMode();
    };
    window.addEventListener('focus', handleFocus);

    // Refetch when page becomes visible (mobile browsers, background tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchMaintenanceMode();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Refetch when coming back online (mobile/unreliable connections)
    const handleOnline = () => {
      fetchMaintenanceMode();
    };
    window.addEventListener('online', handleOnline);

    // Refetch on page show (back/forward navigation on mobile)
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // Page was loaded from cache
        fetchMaintenanceMode();
      }
    };
    window.addEventListener('pageshow', handlePageShow);

    // Subscribe to real-time updates
    const channel = supabase.channel(`maintenance-mode-changes-${Math.random().toString(36).slice(2)}`, {
      config: {
        broadcast: { self: true }
      }
    });

    channel
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'site_content',
          filter: 'content_key=eq.maintenance_mode_enabled'
        },
        (payload) => {
          const newRow = payload.new as any;
          setIsEnabled((newRow?.content_value || '') === 'true');
          // Force immediate refetch to be sure
          fetchMaintenanceMode();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'site_content',
          filter: 'content_key=eq.maintenance_message'
        },
        (payload) => {
          const newRow = payload.new as any;
          setMessage(getContentValue(newRow, 'content_value'));
        }
      )
      .subscribe((status) => {
      });

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('pageshow', handlePageShow);
      supabase.removeChannel(channel);
    };
  }, [fetchMaintenanceMode, getContentValue]);

  return { isEnabled, message, isLoading };
};
